import {POST} from '../../../js/modules/fetch.ts';
import {showErrorToast} from '../../../js/modules/toast.ts';
import {toggleElem} from '../../../js/utils/dom.ts';

// The mode select of an organization's or a repository's default push rule (see
// ee/shared/push_rule_default_form.tmpl). It toggles the visibility of the custom rule fields and
// confirms before an action that would discard the currently stored custom rule, or overwrite it
// with a copy of the effective parent rule.
function initPushRuleDefaultModeSelect() {
  const select = document.querySelector<HTMLSelectElement>('#push-rule-default-mode');
  if (!select) return;

  const customFields = document.querySelector<HTMLElement>('#push-rule-default-custom-fields')!;
  const storedMode = select.getAttribute('data-stored-mode')!;
  let confirmedMode = select.value;

  toggleElem(customFields, select.value === 'custom');

  select.addEventListener('change', () => {
    const nextMode = select.value;
    let confirmMessage = '';
    if (nextMode === 'copy') {
      confirmMessage = select.getAttribute('data-confirm-copy')!;
    } else if (nextMode === 'none') {
      confirmMessage = select.getAttribute('data-confirm-none')!;
    } else if ((storedMode === 'custom' || storedMode === 'copy') && nextMode === 'inherit') {
      confirmMessage = select.getAttribute('data-confirm-discard')!;
    }
    if (confirmMessage && !window.confirm(confirmMessage)) {
      select.value = confirmedMode;
    } else {
      confirmedMode = nextMode;
    }
    toggleElem(customFields, select.value === 'custom');
  });
}

type PushRuleTestCheck = {kind: 'branch_name' | 'author_email' | 'file_name', ok: boolean};
type PushRuleTestResult = {
  source: string, source_id: number, kind: string, name: string,
  message_tested: boolean, required_ok: boolean, forbidden_ok: boolean, checks: PushRuleTestCheck[],
};
type PushRuleTestResponse = {results: PushRuleTestResult[], passed: boolean};

function pushRuleTestResultOK(result: PushRuleTestResult): boolean {
  return result.required_ok && result.forbidden_ok && result.checks.every((check) => check.ok);
}

// pushRuleTestDetails lists the localized outcome of every tested part of one rule.
function pushRuleTestDetails(resultElem: HTMLElement, result: PushRuleTestResult): string[] {
  const details: string[] = [];
  if (result.message_tested) {
    details.push(
      resultElem.getAttribute(result.required_ok ? 'data-locale-required-ok' : 'data-locale-required-fail')!,
      resultElem.getAttribute(result.forbidden_ok ? 'data-locale-forbidden-ok' : 'data-locale-forbidden-fail')!,
    );
  }
  for (const check of result.checks) {
    const kind = check.kind.replaceAll('_', '-');
    details.push(resultElem.getAttribute(`data-locale-${kind}-${check.ok ? 'ok' : 'fail'}`)!);
  }
  return details;
}

function renderPushRuleTestResults(resultElem: HTMLElement, data: PushRuleTestResponse) {
  resultElem.replaceChildren();
  if (!data.results.length) {
    const empty = document.createElement('p');
    empty.className = 'help';
    empty.textContent = resultElem.getAttribute('data-locale-empty')!;
    resultElem.append(empty);
    return;
  }
  for (const result of data.results) {
    const details = pushRuleTestDetails(resultElem, result);
    if (!details.length) continue; // the rule checks none of the filled fields
    const row = document.createElement('div');
    row.className = `ui ${pushRuleTestResultOK(result) ? 'positive' : 'negative'} message`;
    const title = document.createElement('div');
    title.textContent = `${result.name || result.kind} (${result.source})`;
    row.append(title);
    const detail = document.createElement('div');
    detail.textContent = details.join(' \u00b7 ');
    row.append(detail);
    resultElem.append(row);
  }
  const final = document.createElement('div');
  final.className = `ui ${data.passed ? 'positive' : 'negative'} message`;
  final.textContent = resultElem.getAttribute(data.passed ? 'data-locale-final-pass' : 'data-locale-final-fail')!;
  resultElem.append(final);
}

// appendDraftRule reads one push rule form (the mandatory or the default rule form) by its field
// ids and appends its current, possibly unsaved, values to params under "{key}_*". It returns
// false without changing params when the page has no such form, for example a repository has no
// mandatory rule form of its own.
function appendDraftRule(params: URLSearchParams, key: string, prefix: string): boolean {
  const enabled = document.querySelector<HTMLInputElement>(`#${prefix}-enabled`);
  if (!enabled) return false;
  params.set(`${key}_enabled`, enabled.checked ? '1' : '');
  params.set(`${key}_name`, document.querySelector<HTMLInputElement>(`#${prefix}-name`)!.value);
  params.set(`${key}_required`, document.querySelector<HTMLInputElement>(`#${prefix}-required`)!.value);
  params.set(`${key}_forbidden`, document.querySelector<HTMLInputElement>(`#${prefix}-forbidden`)!.value);
  params.set(`${key}_full_message`, document.querySelector<HTMLInputElement>(`#${prefix}-full-message`)!.checked ? '1' : '');
  params.set(`${key}_description`, document.querySelector<HTMLTextAreaElement>(`#${prefix}-description`)!.value);
  params.set(`${key}_example`, document.querySelector<HTMLTextAreaElement>(`#${prefix}-example`)!.value);
  params.set(`${key}_branch_name`, document.querySelector<HTMLInputElement>(`#${prefix}-branch-name`)!.value);
  params.set(`${key}_author_email`, document.querySelector<HTMLInputElement>(`#${prefix}-author-email`)!.value);
  params.set(`${key}_prohibited_file_names`, document.querySelector<HTMLInputElement>(`#${prefix}-prohibited-file-names`)!.value);
  params.set(`${key}_max_file_size_mb`, document.querySelector<HTMLInputElement>(`#${prefix}-max-file-size`)!.value);
  for (const exempt of document.querySelectorAll<HTMLInputElement>(`input[id^="${CSS.escape(`${prefix}-agent-exempt-`)}"]:checked`)) {
    params.append(`${key}_agent_exempt`, exempt.value);
  }
  params.set(`${key}_agent_branch_template`, document.querySelector<HTMLInputElement>(`#${prefix}-agent-branch-template`)!.value);
  return true;
}

// The preview on the push rule settings pages (see ee/shared/push_rule_test.tmpl), one per page.
function initPushRuleTest() {
  const runButton = document.querySelector<HTMLButtonElement>('#push-rule-test-run');
  if (!runButton) return;

  const messageElem = document.querySelector<HTMLTextAreaElement>('#push-rule-test-message')!;
  const branchNameElem = document.querySelector<HTMLInputElement>('#push-rule-test-branch-name')!;
  const authorEmailElem = document.querySelector<HTMLInputElement>('#push-rule-test-author-email')!;
  const filePathElem = document.querySelector<HTMLInputElement>('#push-rule-test-file-path')!;
  const resultElem = document.querySelector<HTMLElement>('#push-rule-test-result')!;

  runButton.addEventListener('click', async () => {
    resultElem.replaceChildren();
    runButton.disabled = true;
    try {
      const params = new URLSearchParams({
        message: messageElem.value,
        branch_name: branchNameElem.value,
        author_email: authorEmailElem.value,
        file_path: filePathElem.value,
      });
      appendDraftRule(params, 'mandatory', 'push-rule-mandatory');
      if (appendDraftRule(params, 'default', 'push-rule-default')) {
        const mode = document.querySelector<HTMLSelectElement>('#push-rule-default-mode');
        params.set('default_mode', mode ? mode.value : 'custom');
      }
      const response = await POST(runButton.getAttribute('data-url')!, {
        data: params,
      });
      const data = await response.json();
      if (!response.ok) {
        showErrorToast(data.errorMessage ?? response.statusText);
        return;
      }
      renderPushRuleTestResults(resultElem, data);
    } catch (err) {
      showErrorToast(String(err));
    } finally {
      runButton.disabled = false;
    }
  });
}

type PushRulePresetCombination = {keys: string, required: string, description: string, example: string};

// The format checkboxes of the rule forms (see ee/shared/push_rule_fields.tmpl), backed by the
// combinations rendered once per page (see ee/shared/push_rule_scope.tmpl). Filling only changes the
// form; nothing is saved until the form is submitted.
function initPushRulePresets() {
  const dataElem = document.querySelector<HTMLElement>('#push-rule-presets');
  if (!dataElem) return;
  const combinations: PushRulePresetCombination[] = JSON.parse(dataElem.getAttribute('data-combinations')!);
  for (const container of document.querySelectorAll<HTMLElement>('.push-rule-presets')) {
    const prefix = container.getAttribute('data-prefix')!;
    const checkboxes = Array.from(container.querySelectorAll<HTMLInputElement>('input[type="checkbox"]'));
    const applyButton = container.querySelector<HTMLButtonElement>('.push-rule-preset-apply')!;
    const checked = () => checkboxes.filter((c) => c.checked);
    for (const checkbox of checkboxes) {
      checkbox.addEventListener('change', () => {
        applyButton.disabled = checked().length === 0;
      });
    }
    applyButton.addEventListener('click', () => {
      const selected = checked();
      if (!selected.length || !window.confirm(container.getAttribute('data-confirm')!)) return;
      const keys = selected.map((c) => c.value).join('|');
      const combined = combinations.find((combination) => combination.keys === keys)!;
      const labels = selected.map((c) => c.labels![0].textContent.trim());
      document.querySelector<HTMLInputElement>(`#${prefix}-name`)!.value = labels.join(' / ');
      document.querySelector<HTMLInputElement>(`#${prefix}-required`)!.value = combined.required;
      // The formats describe commit titles.
      document.querySelector<HTMLInputElement>(`#${prefix}-full-message`)!.checked = false;
      document.querySelector<HTMLTextAreaElement>(`#${prefix}-description`)!.value = combined.description;
      document.querySelector<HTMLTextAreaElement>(`#${prefix}-example`)!.value = combined.example;
    });
  }
}

export function initPushRules() {
  initPushRuleDefaultModeSelect();
  initPushRulePresets();
  initPushRuleTest();
}
